import NextIcon from '@Resources/assets/icons/dark/next.svg';
import PreviousIcon from '@Resources/assets/icons/dark/prev.svg';
import RefreshIcon from '@Resources/assets/icons/dark/refresh.svg';
import SwapIcon from '@Resources/assets/icons/dark/swap.svg';
import {
    convertRadiansToDegrees,
    isValidNumber,
} from '@Utils/helpers/numericalHelpers';
import { SvgIcon } from '@mui/material';
import _isNil from 'lodash/isNil';
import React from 'react';
import { useSelector } from 'react-redux';
import IconBtn from '@Components/IconButton/IconButton';
import IconButton from '@Components/IconButton/IconButton';
import { QualityControlApi } from '@Data/qualityControl/Api';
import {
    adjacentSpansState,
    backPoleState,
    forePoleState,
} from '@Data/qualityControl/Reducer';
import Styles from './ImageCarousel.scss';

interface IImageCarouselProps {
    width: number;
    height: number;
    offset: number;
    index: number;
    title: string;
    onChangeImage: (index: number, isNext: boolean) => void;
    borderColor: string;
    style?: React.CSSProperties;
    bearing?: number | null;
    distance?: string | null;
}

export const ImageCarousel = (props: IImageCarouselProps) => {
    const { index } = props;

    const hasExtraBtn = index === 0 || index === 2 || index === 4;
    const adjacentSpans = useSelector(adjacentSpansState);
    const backPole = useSelector(backPoleState);
    const forePole = useSelector(forePoleState);

    const getButtonValues = (photoFrameIndex) => {
        switch (photoFrameIndex) {
            case 0:
                return {
                    icon: (
                        <SvgIcon inheritViewBox>
                            <RefreshIcon />
                        </SvgIcon>
                    ),
                    tooltipText: 'Change pole and span',
                    disabled: adjacentSpans.length <= 2,
                    onClick: QualityControlApi.cycleNextBackPole,
                };
            case 2:
                return {
                    icon: (
                        <SvgIcon inheritViewBox>
                            <SwapIcon />
                        </SvgIcon>
                    ),
                    tooltipText: 'Swap spans',
                    disabled:
                        adjacentSpans.length < 2 ||
                        _isNil(backPole.entityVersionId) ||
                        _isNil(forePole.entityVersionId),
                    onClick: QualityControlApi.switchSpanIndicies,
                };
            case 4:
                return {
                    icon: (
                        <SvgIcon inheritViewBox>
                            <RefreshIcon />
                        </SvgIcon>
                    ),
                    tooltipText: 'Change pole and span',
                    disabled: adjacentSpans.length <= 2,
                    onClick: QualityControlApi.cycleNextForePole,
                };
            default:
                return {
                    icon: null,
                    tooltip: '',
                };
        }
    };

    const btnValues = getButtonValues(index);

    const handleClick = (isNext: boolean) => {
        props.onChangeImage(props.index, isNext);
    };

    if (
        !isValidNumber(props.width) ||
        !isValidNumber(props.height) ||
        !isValidNumber(props.offset)
    ) {
        return null;
    }

    return (
        <div
            style={{
                width: props.width,
                height: props.height,
                marginLeft: props.offset,
                border: `3px solid ${props.borderColor}`,
                ...props.style,
            }}
            className={Styles.carouselContainer}
        >
            <div className={Styles.carouselContainerLeft}>
                <IconBtn
                    onClick={() => handleClick(false)}
                    icon={
                        <SvgIcon inheritViewBox>
                            <PreviousIcon />
                        </SvgIcon>
                    }
                    tooltip="Previous Image"
                    style={{ padding: '0px', width: '20px' }}
                />
                {hasExtraBtn && (
                    <IconButton
                        onClick={btnValues.onClick}
                        icon={btnValues.icon}
                        tooltip={btnValues.tooltipText}
                        disabled={btnValues.disabled}
                        style={
                            btnValues.disabled
                                ? { cursor: 'not-allowed' }
                                : { cursor: 'pointer' }
                        }
                    />
                )}
            </div>
            <div className={Styles.carouselTextContainer}>
                <span className={Styles.carouselTitle}>{props.title}</span>
                <div className={Styles.bearingDistanceContainer}>
                    {props.distance && <span>distance: {props.distance}</span>}
                    {props.bearing && (
                        <span>
                            {`bearing: ${convertRadiansToDegrees(
                                props.bearing
                            ).toFixed(0)}\u{00B0}`}
                        </span>
                    )}
                </div>
            </div>
            <IconBtn
                onClick={() => handleClick(true)}
                icon={
                    <SvgIcon inheritViewBox>
                        <NextIcon />
                    </SvgIcon>
                }
                tooltip="Next Image"
                style={{ padding: '0px', width: '20px' }}
            />
        </div>
    );
};
